import { useQuery } from '@tanstack/react-query';

import { Alert, Box, CircularProgress, IconButton, Paper, Stack, Tooltip, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';

import { useApiClient } from '@@common';
import CopyToClipboardButton from '../../components/Clipboard/CopyToClipboardButton';
import StyledMarkdown from '../../common/components/StyledMarkdown';
import { ServiceConnectionClient } from '../../api/api';

export default function ServiceConnectionManualView() {
	const [searchParams] = useSearchParams();
	const type = searchParams.get('type') ?? '';
	const client = useApiClient(ServiceConnectionClient);
	const manualQuery = useQuery({
		queryKey: ['serviceConnectionManual', type],
		queryFn: ({ signal }) => client.getManual(type, signal),
		enabled: Boolean(type),
		staleTime: 5 * 60 * 1000,
	});

	return (
		<Stack sx={{ flex: 1, minHeight: 0, p: { xs: 2, md: 4 }, gap: 2 }}>
			<Stack direction="row" alignItems="center" gap={1}>
				<Tooltip title="Back to service connections">
					<IconButton component={RouterLink} to="/service-connections" aria-label="Back to service connections">
						<ArrowBackIcon />
					</IconButton>
				</Tooltip>
				<Box sx={{ flex: 1, minWidth: 0 }}>
					<Typography variant="h5">{type ? `${type} Manual` : 'Service Connection Manual'}</Typography>
					{type && <Typography variant="body2" color="text.secondary">Full setup and usage guide</Typography>}
				</Box>
				<CopyToClipboardButton value={manualQuery.data ?? ''} title="Copy manual to clipboard" disabled={!manualQuery.data} edge={false} />
			</Stack>
			<Paper variant="outlined" sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: { xs: 2, md: 4 }, borderRadius: 2 }}>
				{!type ? (
					<Alert severity="error">No service connection type was specified.</Alert>
				) : manualQuery.isLoading ? (
					<Stack direction="row" alignItems="center" justifyContent="center" gap={2} sx={{ minHeight: 200 }}>
						<CircularProgress size={22} />
						<Typography color="text.secondary">Loading manual…</Typography>
					</Stack>
				) : manualQuery.isError ? (
					<Alert severity="error">Could not load this service connection manual.</Alert>
				) : manualQuery.data?.trim() ? (
					<StyledMarkdown>{manualQuery.data}</StyledMarkdown>
				) : (
					<Typography color="text.secondary">No manual is available for this service connection.</Typography>
				)}
			</Paper>
		</Stack>
	);
}
